// Simple HL7 parser class
export class HL7Data
{
    segments: string[]|undefined;
    fieldDelimit: string|undefined;
    componentDelimit: string|undefined;
    recordDelimit: string|undefined;
    allowMultipleHL7Datasets: boolean;

    constructor(data: string|undefined, allowMultipleHL7Datasets: boolean = false)
    {
        this.allowMultipleHL7Datasets = allowMultipleHL7Datasets;
        if (!data)
        {
            return;
        }

        data = data.trimStart();

        // Where does the MSH segment start?
        const mshRegex = /^MSH/;
        const mshPosMatch = mshRegex.exec(data);
        if (!mshPosMatch)
        {
            return;
        }

        // Get the delimits that follow "MSH"
        this.fieldDelimit =  data.at(3);
		this.componentDelimit = data.at(4);

        // Look for the PID segment
        const pidText = "PID" + this.fieldDelimit;
        const pidPos = data.indexOf(pidText);
        if (pidPos < 0)
        {
            return;
        }

		this.recordDelimit = data.substring(pidPos -1, pidPos);

        // Split the data into segments
        this.segments = data?.split(this.recordDelimit).filter(line => line.trim() !== "");
    }

    // Is the HL7 data valid?
    public get isValid(): boolean
    {
        // If we don't have a delimit, we don't have valid HL7
        return undefined === this.getHl7ValidationIssue();
    }

    // Get any issues trying to validate HL7 data
    public getHl7ValidationIssue(): string[]|undefined
    {
        const issues: string[] = [];
        if (!this.fieldDelimit)
        {
            issues.push("The file does not appear to be HL7 data - unable to determine field delimiter");
        }

        if (!this.componentDelimit)
        {
            issues.push("The file does not appear to be HL7 data - unable to determine component delimiter");
        }

        if (!this.recordDelimit)
        {
            issues.push("The file does not appear to be HL7 data - unable to determine record delimiter");
        }
        
        // Make sure the delimits are unique
        const delimitSet = new Set();
        delimitSet.add(this.fieldDelimit);
        delimitSet.add(this.componentDelimit);
        delimitSet.add(this.recordDelimit);
        if (3 !== delimitSet.size)
        {
            issues.push(`The delmits must be unique. field: ${this.fieldDelimit}, component: ${this,this.componentDelimit}, record: ${this.recordDelimit}`);
        }

        if (!this.allowMultipleHL7Datasets)
        {
            // Look for a single MSH
            if (undefined === this.getRawMSHSegmentString())
            {
                issues.push("The file does not appear to be HL7 data - missing MSH segment");
            }
    
            // Look for a single PID
            if (undefined === this.getRawPIDSegmentString())
            {
                issues.push("The file does not appear to be HL7 data - missing PID segment");
            }
        }
        else
        {
            if (!this.hasSegment(HL7SegmentNames.MSH))            
                issues.push("The file does not appear to be HL7 data - missing MSH segment");

            if (!this.hasSegment(HL7SegmentNames.PID))
            {
                issues.push("The file does not appear to be HL7 data - missing PID segment");
            }

        }
        
        if (issues.length)
        {
            return issues;
        }
    }


    // Does the HL7 have the segments we expect in an order result message
    public get isHL7OrderResultData(): boolean
    {
        return undefined == this.getOrderResultValidationIssue();   
    }

    // Get any issues trying to validate HL7 result data
    public getOrderResultValidationIssue(): string|undefined
    {
        if (!this.isValid)
        {
            if (undefined === this.getRawMSHSegmentString())
            {
                return "The file does not appear to be HL7 data - missing MSH segment";
            }

            if (undefined === this.fieldDelimit)
            {
                return "The file does not appear to be HL7 data - unable to determine field delimiter";
            }

            return "The file does not appear to be HL7 data - reason unknown";
        }

        const mshSegment = this.getSegments(HL7SegmentNames.MSH);
        if (1 != mshSegment.length)
            return "Missing MSH segment";

        // Check the message type
        const msgType = mshSegment[0].field(HL7FieldMSH.MessageType);
        if (!msgType?.startsWith("ORU") && !msgType?.startsWith("ORM"))
        {
            if ("" === msgType)
            {
                return "Unable to validate message type. Expected ORU or ORM.  Unable to find message type.";
            }

            return `Unable to validate message type. Expected ORU or ORM.  Found ${msgType ?? "no message type"} instead.`;
        }

        // Is there a PID segment
        if (!this.hasSegment(HL7SegmentNames.PID))
            return "Missing PID segment";
            
        // We expect certain segments
        if (!this.hasSegment(HL7SegmentNames.OBR) && !this.hasSegment(HL7SegmentNames.OBX) && !this.hasSegment(HL7SegmentNames.NTE))
            return "Expected OBR, OBX, or NTE segment";
    }


    // Does the file have a segment of the specified type
    public hasSegment(segmentName: HL7SegmentNames): boolean
    {
        if (!this.segments)
            return false;

        return !!this.segments.find(item => item.startsWith(segmentName + this.fieldDelimit));
    }

    // Get the raw segment string
    public getRawSegmentStrings(segmentName: HL7SegmentNames): string[]
    {
        if (!this.segments)
            return [];

        return this.segments.filter(item => { 
            // We may not yet have the delimit when working with the MSH segment
            if (HL7SegmentNames.MSH === segmentName && undefined === this.fieldDelimit)
            {
                return item.startsWith(segmentName);
            }
            else
            {
                return item.startsWith(segmentName + this.fieldDelimit);
            }
        });
    }

    // Return the raw MSH segment
    getRawMSHSegmentString(): string|undefined
    {
        const segment = this.getRawSegmentStrings(HL7SegmentNames.MSH);
        if (1 === segment.length)
            return segment[0];
    }

    // Return the raw (single) PID segment
    getRawPIDSegmentString(): string|undefined
    {
        const segment = this.getRawSegmentStrings(HL7SegmentNames.PID);
        if (1 === segment.length)
            return segment[0];
    }


    // Get all the segments for the specified segment name
    public getSegments(segmentName: HL7SegmentNames): HL7Segment[]
    {
        if (!this.segments)
            return [];

        return this.getRawSegmentStrings(segmentName).map((segmentData) => {
            // Parse the segment data into fields
            const fields = this.parseSegmentIntoFields(segmentData);
            return new HL7Segment(fields);
        });
    }    

    // Break the segment into an array of fields
    public parseSegmentIntoFields(segmentData: string|undefined|null) : string[]|undefined
    {
        if (!segmentData || !this.fieldDelimit)
            return undefined;

        // Split the segment data by the field delimiter
        const fields = segmentData.split(this.fieldDelimit);
        return fields;
    }
}

export class HL7Segment
{
    readonly fields: string[]|undefined;

    constructor(fields: string[]|undefined)
    {
        this.fields = fields;
    }

    field(index: number): string|undefined
    {
        if (!this.fields || index < 0 || index >= this.fields.length)
            return undefined;

        return this.fields[index];
    }

    // Get what type of segment we're dealiing with
    public segmentType(): HL7SegmentNames | undefined
    {
        if (!this.fields || this.fields.length < 1)
            return undefined;

        // The segment type is the first field
        const segmentType = this.fields[0];
        return HL7SegmentNames[segmentType as keyof typeof HL7SegmentNames] || undefined;
    }
}

export enum HL7SegmentNames
{
    MSH = "MSH",
    PID = "PID",
    OBR = "OBR",
    OBX = "OBX",
    NTE = "NTE",
    ORC = "ORC",
    DG1 = "DG1",
    PRT = "PRT",
    AL1 = "AL1",
    RXO = "RXO",
    RXR = "RXR",
    RXC = "RXC",
    RXD = "RXD",
    ADT = "ADT"
}

export enum HL7FieldMSH
{
    SendingApplication = 2,
    SendingFacility = 3,
    ReceivingApplication = 4,
    ReceivingFacility = 5,
    DateTimeOfMessage = 6,
    Security = 7,
    MessageType = 8,
    MessageControlID = 9,
    ProcessingID = 10,
    VersionID = 11    
}